import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  PopoverProps,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {
  DateCalendar,
  DateField,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {t} from 'i18next';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {isEmpty} from '../../../../utils/utils';
import AurButton from '../../../common/aur-button/AurButton';
import AurIcon, {IconNames} from '../../../common/aur-icon/AurIcon';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import {ReactComponent as ClearIcon} from '../../../../assests/svg/clear-Icon-filled.svg';

dayjs.extend(utc);

const conditions = [
  'Not set',
  'Is',
  'Is before',
  'Is before or on',
  'Is after',
  'Is after or on',
  'Between',
] as const;
const conditionToTextKey: Record<Condition, string> = {
  'Not set': 'common.aurDatePicker.conditions.notSet',
  Is: 'common.aurDatePicker.conditions.is',
  'Is before': 'common.aurDatePicker.conditions.isBefore',
  'Is before or on': 'common.aurDatePicker.conditions.isBeforeOrOn',
  'Is after': 'common.aurDatePicker.conditions.isAfter',
  'Is after or on': 'common.aurDatePicker.conditions.isAfterOrOn',
  Between: 'common.aurDatePicker.conditions.between',
};
type AurDatePickerType =
  | 'date-picker'
  | 'date-range-picker'
  | 'query-date-picker';

export type Condition = (typeof conditions)[number];

const gridDateOperatorObj: Record<Condition, string[]> = {
  'Not set': [],
  Is: ['is'],
  'Is before': ['before'],
  'Is before or on': ['onOrBefore'],
  'Is after': ['after'],
  'Is after or on': ['onOrAfter'],
  Between: ['onOrAfter', 'onOrBefore'],
};

export type SelectedDateType = Dayjs | null;

interface AurDatePickerProps {
  type?: AurDatePickerType;
  popoverProps?: PopoverProps;
  dateFormat?: string;
  placeholder?: string;
  selectedDate?: SelectedDateType[];
  selectedDateCondition?: Condition;
  advanceLink?: boolean;
  columnField: string;
  onApplyClick?: (
    selectedDate: SelectedDateType[],
    operators: string[],
    selectedCondition?: Condition
  ) => void;
  onCancelClick?: () => void;
  onClearClick?: (selectedCondition?: Condition) => void;
  inputClass?: string;
  inputType?: 'text' | 'search';
  caretIconContainerClass?: string;
  excludeConditions?: string[];
  // Set it true if required to show clear button when Not Set filter is applied
  applyNotSetToFilter?: boolean;
  dataTestId?: string;
  inputTabIndex?: number;
  disablePortal?: boolean;
}

const MenuProps = {
  PaperProps: {
    classes: {
      root: 'aur-conditions-menu-props',
    },
  },
  disableScrollLock: true,
};

const FieldPopoverDateFilter: React.FC<AurDatePickerProps> = ({
  type = 'date-picker',
  popoverProps,
  dateFormat = 'YYYY/MM/DD',
  selectedDate = [],
  selectedDateCondition = 'Is',
  advanceLink = true,
  columnField,
  onApplyClick,
  onCancelClick,
  onClearClick,
  inputClass,
  inputType = 'search',
  caretIconContainerClass,
  excludeConditions = ['Not set'],
  applyNotSetToFilter = false,
  dataTestId,
  inputTabIndex,
  disablePortal = false
}) => {
  const [selectedStartDate, selectedEndDate] = selectedDate;
  const inputRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [condition, setCondition] = useState<Condition | undefined>(
    selectedDateCondition
  );
  const [startDate, setStartDate] = useState<Dayjs | null>(selectedStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(selectedEndDate);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isQueryDatePicker = useMemo(() => type === 'query-date-picker', [type]);
  const isApplied = useRef<boolean>(false);

  const isConditionSelected = useMemo(() => !isEmpty(condition), [condition]);

  const visibleConditionOptions = useMemo(() => {
    return conditions.filter((item) => !excludeConditions?.includes(item));
  }, [excludeConditions]);

  useEffect(() => {
    setStartDate(selectedStartDate);
  }, [selectedStartDate]);

  useEffect(() => {
    setEndDate(selectedEndDate);
  }, [selectedEndDate]);

  useEffect(() => {
    setCondition(selectedDateCondition);
  }, [selectedDateCondition]);

  useEffect(() => {
    if (!anchorEl) return;

    requestAnimationFrame(() => {
      const firstFocusable =
        calendarRef.current?.querySelector(
          'button:not([disabled])'
        ) as HTMLElement | null;

      firstFocusable?.focus();
    });
  }, [anchorEl]);

  const resetDate = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    onClearClick?.(condition);
  }, [condition, onClearClick]);

  const handleConditionChange = useCallback(
    (event: SelectChangeEvent) => {
      if (!isConditionSelected) {
        setStartDate(null);
        setEndDate(null);
      }
      setCondition(event.target.value as Condition);
    },
    [isConditionSelected]
  );

  const handleStartDateChange = useCallback((date: Dayjs | null) => {
    if (date) setStartDate(date);
  }, []);

  const handleEndDateChange = useCallback((date: Dayjs | null) => {
    if (date) setEndDate(date);
  }, []);

  const handleApply = useCallback(() => {
    isApplied.current = true;
    if (!isConditionSelected) {
      resetDate();
    } else {
      onApplyClick?.(
        !isConditionSelected ? [] : [startDate, endDate],
        condition ? gridDateOperatorObj[condition] : [],
        condition
      );
    }
    setAnchorEl(null);
  }, [
    condition,
    endDate,
    isConditionSelected,
    onApplyClick,
    resetDate,
    startDate,
  ]);

  const handleCancel = useCallback(() => {
    if (selectedDate.length === 0) {
      setStartDate(null);
      setEndDate(null);
    }
    onCancelClick?.();
    setAnchorEl(null);
  }, [selectedDate.length, onCancelClick]);

  const handleExited = useCallback(() => {
    if (!isApplied.current) {
      setCondition(selectedDateCondition);
      setStartDate(selectedStartDate);
      setEndDate(selectedEndDate);
    }
    if (isApplied.current) {
      isApplied.current = false;
    }
  }, [selectedDateCondition, selectedEndDate, selectedStartDate]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    },
    [anchorEl, setAnchorEl]
  );

  const shouldShowClearButton = useMemo(() => {
    let showClear =
      inputType === 'text' && isConditionSelected && selectedDate.length > 0;
    if (!showClear && condition === 'Not set' && applyNotSetToFilter)
      showClear = true;
    return showClear;
  }, [inputType, selectedDate, condition, applyNotSetToFilter]);

  const shouldShowRangeCalendar = useMemo(
    () =>
      type === 'date-range-picker' ||
      (isQueryDatePicker && condition === 'Between'),
    [type, isQueryDatePicker, condition]
  );

  const getDateValue = useMemo(() => {
    let dateValue;
    if (shouldShowRangeCalendar) {
      const start = selectedStartDate?.format(dateFormat);
      const end = selectedEndDate?.format(dateFormat);
      dateValue = start;
      if (end) {
        dateValue += ' - ' + end;
      }
    } else {
      dateValue = selectedStartDate?.format(dateFormat) ?? '';
    }
    return dateValue;
  }, [shouldShowRangeCalendar, selectedStartDate, dateFormat, selectedEndDate]);

  const isApplyButtonDisabled = useMemo(() => {
    if (isConditionSelected && condition === 'Not set') return false;
    return shouldShowRangeCalendar
      ? (isEmpty(startDate) ||
          isEmpty(endDate) ||
          !startDate?.isValid() ||
          !endDate?.isValid() ||
          startDate.isAfter(dayjs()) ||
          endDate.isAfter(dayjs()) ||
          endDate.isBefore(startDate, 'day')) &&
          isConditionSelected
      : isEmpty(startDate) && isConditionSelected;
  }, [
    shouldShowRangeCalendar,
    startDate,
    endDate,
    isConditionSelected,
    condition,
  ]);

  const renderConditions = useMemo(() => {
    return (
      isQueryDatePicker && (
        <Select
          fullWidth
          className={'aur-conditions-dropdown-container'}
          value={condition}
          onChange={handleConditionChange}
          MenuProps={{...MenuProps, disablePortal}}
          sx={{
            width:
              type === 'date-range-picker' || condition === 'Between'
                ? '47.5%'
                : '100%',
          }}
          inputProps={{
            'aria-label': t('common.aurDatePicker.dropdownButton'),
          }}
          renderValue={() => (
            <Typography className="typo-text body3_size">
              {condition ? t(conditionToTextKey[condition]) : ''}
            </Typography>
          )}
          data-testid="date-filter-condition-dropdown"
        >
          {visibleConditionOptions.map((cond) => (
            <MenuItem key={cond} value={cond}>
              <Box display="flex" justifyContent="space-between" width="100%">
                <Typography className="typo-text body3_size ">
                  {t(conditionToTextKey[cond])}
                </Typography>
                {[condition].includes(cond) && (
                  <CheckIcon fontSize="small" className="aur-date-check-mark" />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      )
    );
  }, [
    isQueryDatePicker,
    condition,
    handleConditionChange,
    type,
    visibleConditionOptions,
  ]);

  const renderDateRangeField = useMemo(() => {
    return shouldShowRangeCalendar ? (
      <Box className="aur-horizontal-date-container">
        <Box className="aur-date-container" flex={1}>
          <Typography className="typo-text body4_size aur-date-placholder">
            {t('common.aurDatePicker.startDate')}
          </Typography>
          <DateField
            variant="standard"
            value={startDate ?? null}
            format={t('common.yearMonthDate')}
            maxDate={endDate ?? dayjs()}
            InputProps={{
              disableUnderline: true,
              className: 'typo-text body2_size',
            }}
            onChange={handleStartDateChange}
          />
        </Box>

        <Divider orientation="horizontal" flexItem className="aur-divider" />

        <Box className="aur-date-container" flex={1}>
          <Typography className="typo-text body4_size aur-date-placholder">
            {t('common.aurDatePicker.endDate')}
          </Typography>
          <DateField
            variant="standard"
            value={endDate ?? undefined}
            format={t('common.yearMonthDate')}
            minDate={startDate ?? undefined}
            maxDate={dayjs()}
            InputProps={{
              disableUnderline: true,
              className: 'typo-text body2_size',
            }}
            onChange={handleEndDateChange}
          />
        </Box>
      </Box>
    ) : undefined;
  }, [shouldShowRangeCalendar, dateFormat, startDate, endDate]);

  const inputValue = useMemo(() => {
    if (condition === 'Not set') {
      return t('fieldManagement.fieldTable.notSet');
    }

    if (isConditionSelected && selectedDate.length > 0) {
      return getDateValue;
    }

    return '';
  }, [condition, getDateValue, isConditionSelected, selectedDate.length]);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        className={'aur-field-popover-date-filter'}
        onClick={handleClick}
        data-testid={dataTestId}
      >
        <AurTextField
          data-testid={`input-${columnField}`}
          className={`aur-search-input ${inputClass ?? ''}`}
          inputRef={inputRef}
          autoComplete={'off'}
          variant={'filled'}
          ariaLabel={t('fieldManagement.fieldTable.all')}
          size={'small'}
          type={inputType}
          onChange={(event) => {
            if (isEmpty(event.target.value)) {
              resetDate();
              setAnchorEl(null);
            } else {
              inputRef.current?.setSelectionRange(0, 0);
            }
          }}
          placeholder={t('fieldManagement.fieldTable.all')}
          value={inputValue}
          onFocus={() => {
            inputRef.current?.setSelectionRange(0, 0);
          }}
          InputProps={{
            style: {
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            },
            endAdornment: shouldShowClearButton && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={(event) => {
                    event.stopPropagation();
                    resetDate();
                    setAnchorEl(null);
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          tabIndex={inputTabIndex}
        />
        <Box className={`aur-caret-icon-container ${caretIconContainerClass}`}>
          <IconButton
            className={'aur-caret-icon-button'}
            onClick={handleClick}
            aria-label={t('buttonGroup.dateFilter')}
            data-testid={`dropdown-${columnField}`}
            tabIndex={0}
          >
            <AurIcon
              icon={anchorEl ? IconNames.CaretUp : IconNames.CaretDown}
            />
          </IconButton>
        </Box>
      </Box>

      <Popover
        className={'aur-date-picker-popper-filter'}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        {...popoverProps}
        onClose={handleCancel}
        TransitionProps={{
          onExited: handleExited,
        }}
      >
        <div role="region">
        <Paper className="date-paper">
          {renderConditions}
          {renderDateRangeField}
          <Box className={'aur-calendar-container'} ref={calendarRef}>
            <DateCalendar
              value={startDate ?? null}
              maxDate={
                shouldShowRangeCalendar && !isEmpty(endDate)
                  ? (endDate ?? dayjs())
                  : dayjs()
              }
              onChange={handleStartDateChange}
              className={'aur-calendar'}
              disabled={condition === 'Not set' || !isConditionSelected}
            />
            {shouldShowRangeCalendar && (
              <DateCalendar
                value={endDate ?? null}
                minDate={startDate ?? undefined}
                onChange={handleEndDateChange}
                className={'aur-calendar'}
                disabled={!isConditionSelected}
                maxDate={dayjs()}
              />
            )}
          </Box>
          {advanceLink ? (
            <AurButton
              classNames={'aur-date-advanced-button-filter'}
              endIcon={<AurIcon icon={IconNames.OpenInNewIcon} />}
              type={'text'}
              size={'Small'}
              label={t('common.aurDatePicker.advanced')}
              dataTestId={'aur-advanced'}
            />
          ) : null}
          <Box className={'aur-date-footer-filter'}>
            <AurButton
              classNames={'aur-date-cancel-button-filter'}
              type={'text'}
              title={t('common.aurDatePicker.cancel')}
              label={t('common.aurDatePicker.cancel')}
              size={'Medium'}
              onClick={handleCancel}
              dataTestId={'Clear-button'}
            />
            <AurButton
              classNames={'aur-date-apply-button-filter'}
              type="continue"
              title={t('common.aurDatePicker.apply')}
              label={t('common.aurDatePicker.apply')}
              size={'Medium'}
              onClick={handleApply}
              dataTestId={'aur-date-apply-filter-button'}
              disabled={isApplyButtonDisabled}
            />
          </Box>
        </Paper>
        </div>
      </Popover>
    </LocalizationProvider>
  );
};

export default FieldPopoverDateFilter;
